import { AuthenticationModuleClientType } from '@hendrx/authentication/contracts/client';
import { AuthorizationModuleClientType } from '@hendrx/authorization/contracts/client';
import { FileStorageModuleClientType } from '@hendrx/file-storage/contracts/client';
import { LmsModuleClientType } from '@hendrx/lms/contracts/client';
import { XApiEventsModuleClientType } from '@hendrx/xapi-events/contracts/client';

export type ClientModules = {
  authentication: AuthenticationModuleClientType;
  authorization: AuthorizationModuleClientType;
  fileStorage: FileStorageModuleClientType;
  lms: LmsModuleClientType;
  xAPIEvents: XApiEventsModuleClientType;
};

export type ModulesRegistry<TModules extends ClientModules> = TModules;

export type ClientModulesState = 'idle' | 'initializing' | 'initialized' | 'error';

export function createClientModulesRegistrar<TModules extends ClientModules>() {
  let registeredModules: TModules | null = null;
  let state: ClientModulesState = 'idle';

  const modules = new Proxy({} as TModules, {
    get(_target, moduleName) {
      if (state === 'idle' || state === 'initializing' || !registeredModules) {
        throw new Error('Client modules were accessed before initialization.');
      }

      if (state === 'error') {
        throw new Error('Client modules initialization failed.');
      }

      if (typeof moduleName !== 'string') {
        throw new Error('Client modules were accessed with a non-string property.');
      }

      return registeredModules[moduleName as keyof TModules];
    }
  });

  return {
    async initialize(
      registerModules: (modules: ModulesRegistry<TModules>) => Promise<TModules> | TModules
    ): Promise<TModules> {
      if (state === 'initialized') {
        throw new Error('Client modules have already been initialized.');
      }

      if (state === 'initializing') {
        throw new Error('Client modules are already being initialized.');
      }

      if (state === 'error') {
        throw new Error('Client modules initialization failed.');
      }

      state = 'initializing';

      try {
        registeredModules = await registerModules(modules);
        state = 'initialized';
        return modules;
      } catch (error) {
        registeredModules = null;
        state = 'error';
        throw error;
      }
    },
    getState(): ClientModulesState {
      return state;
    },
    modules
  };
}
